<template>
  <section class="at-risk" data-cy="membership-at-risk-card">
    <header class="at-risk__head">
      <span class="at-risk__eyebrow">At Risk</span>
      <Tooltip placement="top" :width="280">
        <template #trigger>
          <i
            class="fas fa-info-circle at-risk__info"
            aria-label="More information"
          ></i>
        </template>
        <template #tooltip>
          <span class="at-risk__tooltip-text">{{ TOOLTIP }}</span>
        </template>
      </Tooltip>
    </header>

    <div class="at-risk__body">
      <Skeleton
        v-if="loading"
        width="100%"
        height="9rem"
        data-cy="membership-at-risk-skeleton"
      />
      <ul v-else class="at-risk__rows">
        <li
          v-for="row in rows"
          :key="row.key"
          class="at-risk__row-item"
          :data-cy="`at-risk-row-${row.key}`"
        >
          <component
            :is="row.href ? 'a' : 'div'"
            :href="row.href"
            class="at-risk__row"
            :class="{ 'at-risk__row--linked': row.href }"
          >
            <span class="at-risk__label">{{ row.label }}</span>
            <span
              class="at-risk__value"
              :class="`at-risk__value--${row.severity}`"
              >{{ row.value }}</span
            >
          </component>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import { type Severity, severityForRisk } from "./severity";
import Skeleton from "./Skeleton.vue";
import { useMembershipDashboard } from "./useMembershipDashboard";

const TOOLTIP =
  "Members who need proactive outreach within the next 30 days. Click any row to see the filtered list.";

const numberFormatter = new Intl.NumberFormat("en-US");

const { summary, loading, membersReportHref } = useMembershipDashboard();

function next30DayRange(): string {
  const today = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 30);
  const fmt = (d: Date) => {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
  };
  return `${fmt(today)}-${fmt(end)}`;
}

interface Row {
  key: string;
  label: string;
  count: number;
  value: string;
  severity: Severity;
  href: string | undefined;
}

const rows = computed<Row[]>(() => {
  const expiringCount = summary.value?.expiring_cards_count ?? 0;
  const nonRenewingCount = summary.value?.non_renewing_expiring_count ?? 0;

  return [
    {
      key: "expiring-cards",
      label: "Expiring cards",
      count: expiringCount,
      value: numberFormatter.format(expiringCount),
      severity: severityForRisk(expiringCount),
      href: membersReportHref(
        "/reports/members?payment_method_expiring_within=30"
      )
    },
    {
      key: "non-renewing",
      label: "Non-renewing memberships expiring in 30 days",
      count: nonRenewingCount,
      value: numberFormatter.format(nonRenewingCount),
      severity: severityForRisk(nonRenewingCount),
      href: membersReportHref(
        `/reports/members?renewal_type=manual&current_ending_within=${next30DayRange()}`
      )
    }
  ];
});
</script>

<style lang="scss" scoped>
.at-risk {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #fff;
  border: 1px solid var(--medium-color, #e5e7eb);
  border-radius: 6px;
  padding: 1.25rem 1.5rem;
  height: 100%;
}
.at-risk__head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.at-risk__eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #4b5563;
}
.at-risk__info {
  font-size: 0.75rem;
  color: #9ca3af;
  cursor: help;
  &:hover {
    color: #4b5563;
  }
}
.at-risk__tooltip-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}
.at-risk__body {
  flex: 1;
}
.at-risk__rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.at-risk__row-item + .at-risk__row-item {
  border-top: 1px solid #f3f4f6;
}
.at-risk__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  text-decoration: none;
  color: inherit;
}
.at-risk__row--linked {
  cursor: pointer;
  transition: background-color 120ms ease;
  margin: 0 -0.5rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  border-radius: 4px;
  &:hover {
    background-color: #f9fafb;
  }
}
.at-risk__label {
  font-size: 0.875rem;
  color: #374151;
}
.at-risk__value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
.at-risk__value--caution {
  color: #b45309;
}
.at-risk__value--alert {
  color: #b91c1c;
}
</style>
